import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { ensureIndexes } from '/imports/api/ensureIndex';
import { Doc, ListMethodArg, ListMethodResponse, GetMethodResponse, ModelClass, References } from '/imports/types';

export interface TradeItem extends Doc {
  _id: string;
  escrow: string;
  botId: number;
  bot: string;
  partnerId: number;
  partner: string;
  cardId: number;
  card: string;
  qty: number;
  action: 'take' | 'give';
  price: number | null;
  /**
   * Analytics stamp, written by accountant.fileInvoice. Optional because rows
   * predating it (every row before this change) will not have them.
   */
  /** The bot's durable role, NOT the mode it was running at the time. */
  mode?: string;
  /** 'usd' for cash bots, 'tix' for everything else. `price` is in this unit. */
  currency?: 'usd' | 'tix';
  /** MTGO trade number, usable when the `escrow` FK is dangling. */
  tradeno?: number | null;
  /** Type code. With cardId this forms the SKU; premium and regular differ. */
  typ?: number;
  /** True when `price` is a configured floor (defaultBuyPrice on takes,
   *  defaultSellPrice on gives), not a real quote -- the card had no real
   *  price on that side and moved as junk. */
  junk?: boolean;
}

type Model = TradeItem;
type Refs = References<Model>;

export const TradeItems = new Mongo.Collection<TradeItem>('tradeItems');

/**
 * `createdAt`: the dashboard dates legacy rows off the ObjectId hex in `_id`
 * (index-backed for free) but rows written since 79fde25 off `createdAt`,
 * which is the branch that grows from here on.
 *
 * `escrow` and `partnerId`: the Trade Inspector rebuilds a completed trade
 * from its ledger lines by escrow id and prices a partner's whole history by
 * partnerId; both were collection scans.
 *
 * Fire-and-forget outside `Meteor.startup` on purpose: a rejecting startup hook
 * takes the whole server down with `process.exit(1)`; ensureIndexes never throws.
 */
Meteor.isServer && (async () => {
  await ensureIndexes(TradeItems, [
    { keys: { createdAt: -1 }, options: { sparse: true } },
    { keys: { escrow: 1 } },
    { keys: { partnerId: 1, createdAt: -1 } },
  ]);
})();

export const listTradeItems = DeclareMethod<ListMethodArg<TradeItem>, ListMethodResponse<TradeItem, Refs>>('listTradeItems');
export const getTradeItem = DeclareMethod<string, GetMethodResponse<TradeItem, Refs>>('getTradeItem');
export const createTradeItem = DeclareMethod<Partial<TradeItem>, string>('createTradeItem');
export const updateTradeItem = DeclareMethod<Partial<TradeItem>, string>('updateTradeItem');
export const deleteTradeItem = DeclareMethod<string, undefined>('deleteTradeItem');

export const modelClass: ModelClass<Model, Refs> = {
  collection: TradeItems,
  editableFields: ['action', 'qty', 'price'],
  listMethod: listTradeItems,
  getMethod: getTradeItem,
  createMethod: createTradeItem,
  updateMethod: updateTradeItem,
  deleteMethod: deleteTradeItem,
  pathPrefix: '/trade-items',
  modelName: 'TradeItem',
  pluralName: 'TradeItems',
};
